import { computed, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Languages } from '@enums/languages';
import { StorageService } from '@core/services';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly TAG = 'LanguageService';
  private transloco = inject(TranslocoService);
  private storage = inject(StorageService);
  private _activeLanguage: WritableSignal<string> = signal(this.transloco.getActiveLang());
  activeLanguage = computed(() => this._activeLanguage());
  readonly currentLanguage = this._activeLanguage.asReadonly();
  constructor() {
    this.loadLanguageFromStorage();
    this.listenToChanges();
  }

  listenToChanges() {
    // Keep everything in sync whenever the signal changes
    effect(() => {
      const lang = this._activeLanguage();

      // Transloco may have changed elsewhere; make sure it matches our signal
      if (this.transloco.getActiveLang() !== lang) {
        this.transloco.setActiveLang(lang);
        document.documentElement.setAttribute('lang', lang);
        this.storage.setItem('language', lang);
      }
    });

    // If someone else changes Transloco’s lang (outside this service), reflect it back
    this.transloco.langChanges$.subscribe(lang => {
      if (lang && lang !== this._activeLanguage()) {
        this._activeLanguage.set(lang);
        this.storage.setItem('language', lang);
      }
    });
  }
  loadLanguageFromStorage(): void {
    const lang: string = this.storage.getItem('language') ?? 'es';
    //console.log(`${this.TAG} el lenguaje leido es ${lang}`);
    const language: Languages = lang == 'es' ? Languages.es : Languages.en;
    this.changeLanguage(language);
  }

  changeLanguage(lang: Languages): void {
    if (lang.valueOf() !== this._activeLanguage()) {
      this.transloco.setActiveLang(lang.valueOf());
      this._activeLanguage.set(lang.valueOf());
      // keep the <html lang="…"> in sync
      document.documentElement.setAttribute('lang', lang);
    } else {
      //console.log(`${this.TAG} changeLanguage skipped (already '${this._activeLanguage()}')`);
    }
  }

  getCurrentLanguage(): string {
    return this._activeLanguage();
  }

  getCurrentLanguageLocale(): string {
    return `${this._activeLanguage()}-${this._activeLanguage().toUpperCase()}`;
  }
}
